import { useEffect, useState, useRef, useCallback } from 'react';
import { useAppStore } from '@/stores/appStore';
import { Navbar } from '@/components/layout/Navbar';
import { TimelineView } from '@/components/timeline/TimelineView';
import { TimelineWeekView, isDateMode } from '@/components/timeline/TimelineWeekView';
import { PresentationOverlay } from '@/components/timeline/PresentationOverlay';
import { FilterBar } from '@/components/filters/FilterBar';
import { PlayIcon } from '@heroicons/react/24/outline';
import { TimeframeMenu } from '@/components/ui/TimeframeMenu';
import {
  productApi,
  subProductApi,
  initiativeApi,
  classificationApi,
  statusApi,
  tagApi,
  userApi,
  fiscalYearApi,
  fieldConfigApi,
  labsApi,
} from '@/services/api';

type ViewMode = 'weekly' | 'monthly' | 'quarterly';

const VIEW_MODE_KEY = 'cpr_timeline_view_mode';

const TIMEFRAMES: { value: ViewMode; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

export function TimelinePage() {
  const {
    setProducts,
    setInitiatives,
    setClassifications,
    setStatuses,
    setTags,
    setOwners,
    setQuarters,
    setFieldConfigs,
    setLabsFeatures,
    setLoading,
    setError,
    refreshTrigger,
    activeSavedView,
  } = useAppStore();

  const [filteredInitiatives, setFilteredInitiatives] = useState<any[]>([]);
  const viewContainerRef = useRef<HTMLDivElement>(null);

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const stored = sessionStorage.getItem(VIEW_MODE_KEY);
    return (stored === 'weekly' || stored === 'monthly' || stored === 'quarterly') ? stored : 'quarterly';
  });

  const [isPresentationMode, setIsPresentationMode] = useState(false);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    sessionStorage.setItem(VIEW_MODE_KEY, mode);
  };

  const handleExitPresentation = useCallback(() => {
    setIsPresentationMode(false);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [
        productsData,
        subProductsData,
        initiativesData,
        classificationsData,
        statusesData,
        tagsData,
        usersData,
        fiscalYearsData,
        fieldConfigData,
        labsData,
      ] = await Promise.all([
        productApi.getAll(),
        subProductApi.getAll(),
        initiativeApi.getAll(),
        classificationApi.getAll(),
        statusApi.getAll(),
        tagApi.getAll(),
        userApi.getAll(),
        fiscalYearApi.getAll(),
        fieldConfigApi.getAll().catch(() => []),
        labsApi.getAll().catch(() => []),
      ]);

      const productsWithSubProducts = productsData.map((product: any) => ({
        ...product,
        subProducts: subProductsData.filter(
          (sp: any) => sp.productId === product.id
        ),
      }));

      setProducts(productsWithSubProducts);
      setInitiatives(initiativesData);
      setClassifications(classificationsData);
      setStatuses(statusesData);
      setTags(tagsData);
      setOwners(usersData);
      if (fieldConfigData.length > 0) {
        setFieldConfigs(fieldConfigData);
      }
      if (labsData.length > 0) {
        setLabsFeatures(labsData);
      }

      const allQuarters = fiscalYearsData
        .flatMap((fy: any) => fy.quarters || [])
        .sort((a: any, b: any) => a.displayOrder - b.displayOrder);

      setQuarters(allQuarters);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load roadmap data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [refreshTrigger]);

  // Keyboard shortcut: Cmd+Shift+P to enter presentation mode
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'p') {
        e.preventDefault();
        if (filteredInitiatives.length > 0) {
          setIsPresentationMode(true);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [filteredInitiatives.length]);

  return (
    <div className="h-screen flex flex-col bg-background-primary">
      <Navbar />

      <main className="flex-1 overflow-hidden">
        <div className="h-full flex flex-col">
          <div className="px-3 sm:px-6 py-3 bg-background-primary flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <div className="flex-1 min-w-0">
              <FilterBar
                key={activeSavedView?.viewType === 'timeline' ? activeSavedView.id : 'default'}
                onFilteredInitiativesChange={setFilteredInitiatives}
                viewType="timeline"
                viewContainerRef={viewContainerRef}
                savedViewKey="timeline"
                viewMode={viewMode}
                weeklyDatedCount={viewMode === 'weekly' ? filteredInitiatives.filter(isDateMode).length : undefined}
                injectedFilterState={activeSavedView?.viewType === 'timeline' ? activeSavedView.filterState : null}
              />
            </div>
            {/* Present button */}
            <button
              onClick={() => setIsPresentationMode(true)}
              disabled={filteredInitiatives.length === 0}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-gray-100 text-text-secondary hover:bg-gray-200 hover:text-text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex-shrink-0"
              title="Enter presentation mode (⌘⇧P)"
            >
              <PlayIcon className="w-4 h-4" />
              <span className="hidden xl:inline">Present</span>
            </button>
            <TimeframeMenu value={viewMode} options={TIMEFRAMES} onChange={handleViewModeChange} />
          </div>

          <div data-tour-id="timeline-content" className="flex-1 overflow-hidden">
            {viewMode === 'weekly' ? (
              <TimelineWeekView initiatives={filteredInitiatives} containerRef={viewContainerRef} />
            ) : (
              <TimelineView initiatives={filteredInitiatives} containerRef={viewContainerRef} viewMode={viewMode} />
            )}
          </div>
        </div>
      </main>

      {/* Presentation Mode Overlay */}
      {isPresentationMode && (
        <PresentationOverlay
          initiatives={filteredInitiatives}
          onExit={handleExitPresentation}
          viewMode={viewMode === 'weekly' ? 'monthly' : viewMode}
        />
      )}
    </div>
  );
}
